import {
  Campaign,
  GetCampaignParams,
  UseReportsProps,
  useCampaign,
  useReport,
  UseCalculatedFieldsReturn,
  useCalculatedFields,
} from '@features/campaigns';
import { ImageWithMetadata } from '@features/powerpoint';
import { Subdimension } from '@features/reports/subdomains/visitation';
import { useLatestCampaignAnalyticsReport } from '@components/Dashboard/CampaignAnalytics/hooks/useCampaignAnalytics';
import { Lci3Report } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { CampaignAnalyticsReport } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { GetLci3ReportParams } from '@components/Dashboard/dashboardSlice/campaignsApi';

export type LciReportFieldsHook = {
  loading: boolean;
  error?: string;
  data?: {
    campaign: Campaign;
    selectedReportDate: Campaign['reportDates'][number];
    brandLogo: ImageWithMetadata | null;
    salesSummary: Lci3Report['salesMetrics'] | null;
    visitMetrics: Lci3Report['visitMetrics'];
    subdimensions: Subdimension[];
    campaignAnalyticsReport: CampaignAnalyticsReport | null;
    calculatedFields: UseCalculatedFieldsReturn['data'];
  };
};

type FetchParams = {
  campaignParams: GetCampaignParams;
  reportParams: Omit<GetLci3ReportParams, 'id'>;
};

export const useLciReportFields = (
  fetchParams: FetchParams
): LciReportFieldsHook => {
  const { campaign, isFetching: loadingCampaign } = useCampaign(
    fetchParams.campaignParams
  );
  const reportsProps: UseReportsProps = {
    campaignId: campaign?.id,
    campaignLoading: loadingCampaign,
    reportParams: fetchParams.reportParams,
    campaignParams: fetchParams.campaignParams,
  };
  const {
    report,
    subdimensions,
    salesReport,
    loading: loadingReport,
  } = useReport(reportsProps);
  const { data: calculatedFields, isFetching: isFetchingCalculatedFields } =
    useCalculatedFields({
      report,
      salesReport,
      campaign: campaign ?? { startDate: '', endDate: '', id: 0 },
    });
  const visitMetrics = report?.visitMetrics;

  const salesAttributionSummary = salesReport?.salesMetrics ?? null;

  const {
    report: campaignAnalyticsReport,
    isLoading: loadingCampaignAnalyticsReport,
  } = useLatestCampaignAnalyticsReport(fetchParams.campaignParams.id);

  const loading =
    loadingCampaign ||
    loadingReport ||
    loadingCampaignAnalyticsReport ||
    isFetchingCalculatedFields;

  if (!campaign || !report || !visitMetrics)
    return { error: 'Missing required data for PPT generation', loading };

  return {
    loading,
    data: {
      brandLogo: null,
      campaign,
      selectedReportDate: report.reportDate,
      visitMetrics,
      salesSummary: salesAttributionSummary,
      subdimensions,
      campaignAnalyticsReport,
      calculatedFields,
    },
  };
};
